import {
  FieldText,
  FieldTextarea,
  FieldWrapper,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import * as React from 'react';

import type { UserPresence } from '../../../presence/reducers/presence';
import { DispatchProps, StateProps } from '../containers/transferToAgentModal';

import styles from './transferToAgentModal.module.scss';
import UserItem from './userItem';

type Props = StateProps &
  DispatchProps & {
    isLoadingQueueAgents?: boolean;
  };

type State = {
  init: boolean;
  searchValue: string;
  messageValue: string;
  blindRequestToUser: User | null;
};

export default class TransferToAgentModal extends React.PureComponent<
  Props,
  State
> {
  input: React.RefObject<HTMLInputElement>;
  messageInput: React.RefObject<HTMLTextAreaElement>;

  constructor(props: Props) {
    super(props);
    this.input = React.createRef();
    this.messageInput = React.createRef();
    this.state = {
      init: true,
      searchValue: '',
      messageValue: '',
      blindRequestToUser: null,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const {
      ui: { isModalShown, isMessageModalShown },
    } = this.props;
    const {
      ui: {
        isModalShown: nextIsModalShown,
        isMessageModalShown: nextIsMessageModalShown,
      },
    } = nextProps;

    if (isModalShown && !nextIsModalShown) {
      // modal closing
      this.setState({
        init: true,
        searchValue: '',
        blindRequestToUser: null,
      });
    }

    if (isMessageModalShown && !nextIsMessageModalShown) {
      this.setState({
        messageValue: '',
      });
    } else if (!isModalShown && nextIsModalShown) {
      // modal opening
      this.focusOnSearchInput();
    } else if (!isMessageModalShown && nextIsMessageModalShown) {
      this.focusOnMessageInput();
    }
  }

  onAssistedTransferClick = (payload: any) => {
    const { requestToUser } = payload;

    if (requestToUser !== undefined) {
      this._sendAssistedTransferRequest(payload);
    } else {
      console.warn(
        'onAssistedTransferClick no valid user given on payload: ',
        requestToUser,
      );
    }
  };

  onBlindTransferClick = (payload: any) => {
    const { metaConversation, transferToAgentShowMessageModal } = this.props;
    const { requestToUser } = payload;
    const { id } = metaConversation.conversation;

    if (requestToUser !== undefined) {
      if (id) {
        ampli.conversationTransferred({
          conversationId: id,
          option: 'agent',
        });
      }

      this.setState({ blindRequestToUser: requestToUser });
      transferToAgentShowMessageModal();
    } else {
      console.warn(
        'onBlindTransferClick no valid user given on payload: ',
        requestToUser,
      );
    }
  };

  onBlindTransferMessageClick = () => {
    const requestToUser = this.state.blindRequestToUser;
    if (requestToUser) {
      this._sendBlindTransferRequest({ requestToUser: requestToUser });
    } else {
      console.warn(
        'onBlindTransferMessageClick no valid user given on state: ',
        requestToUser,
      );
    }
  };

  _sendBlindTransferRequest = (payload: any) => {
    const { requestToUser } = payload;
    const { messageValue } = this.state;
    const csid = this.props.metaConversation.conversation.id;
    const blindTransferRequest = {
      requestToUser: requestToUser,
      requestFromAgent: this.props.profile,
      metaConversation: this.props.metaConversation,
      message: messageValue,
    };

    this.props
      .transferToAgentBlindRequest(blindTransferRequest)
      .then((response: any) => {
        this.props.transferToAgentModalClose();
        this.props.transferToAgentHideMessageModal();
      })
      .catch((exception: any) => {
        console.warn(exception);
        this.props.transferRequestStop(csid);
        this.props.transferToAgentModalClose();
        const options = {
          title: 'Could not transfer the conversation',
          text: `An issue occurred while trying transfer conversation ${csid}.`,
          className: 'danger',
          icon: 'i-exclamation',
        };
        this.props.notificationAdd(options);
      });
  };

  _sendAssistedTransferRequest = (payload: any) => {
    const { requestToUser } = payload;

    this.props.transferToAgentAssistedRequest({
      requestToUser: requestToUser,
      requestFromAgent: this.props.profile,
      metaConversation: this.props.metaConversation,
    });
  };

  hideTransferToAgentModal = () => {
    this.props.transferToAgentModalClose();
  };

  handleBackToQueueSelection = () => {
    const csid = this.props.metaConversation?.conversation?.id;
    this.props.transferToAgentModalClose();
    if (csid) {
      this.props.openQueueTransferModal(csid);
    }
  };

  hideTransferToAgentModalMessageModal = () => {
    this.props.transferToAgentModalClose();
    this.props.transferToAgentHideMessageModal();
  };

  onChangeInput = (e: any) => {
    this.setState({ init: false, searchValue: e.target.value });
  };

  onChangeMessageValue = (e: any) => {
    this.setState({ messageValue: e.target.value });
  };

  focusOnSearchInput = () => {
    setTimeout(() => {
      if (this.input.current) {
        this.input.current.focus();
      }
    }, 100);
  };

  focusOnMessageInput = () => {
    setTimeout(() => {
      if (this.messageInput && this.messageInput.current) {
        this.messageInput.current.focus();
      }
    }, 100);
  };

  render() {
    const {
      onlinePresence,
      meta,
      metaConversation,
      numberTransferRequests,
      profile,
      transferToAgentRequests,
      ui,
      queueId,
      isLoadingQueueAgents,
    } = this.props;
    const { searchValue } = this.state;
    const disabled = meta.requestAssistedTransfer === 'loading';
    let csid: number | string | undefined = '';
    let loading: JSX.Element | null = null;

    const searchValueTrimmed = searchValue.trim();
    const filteredPresence = onlinePresence.filter(
      (presenceUser: UserPresence) => {
        const { user } = presenceUser;
        let matchesNameOrNoName = !searchValueTrimmed;

        if (searchValueTrimmed) {
          if (user.name !== undefined && user.name !== null) {
            const matchingName = user.name.toUpperCase();
            matchesNameOrNoName =
              matchingName.indexOf(searchValue.toUpperCase()) !== -1;
          }
        }

        return user.id !== profile.id && matchesNameOrNoName;
      },
    );

    if (
      metaConversation !== null &&
      metaConversation.conversation.id !== null
    ) {
      csid = metaConversation.conversation.id;
    }

    const listAgent = (presenceUser: UserPresence, index: number) => {
      const { user, presence } = presenceUser;

      return (
        <UserItem
          key={user.id}
          user={user}
          disabled={disabled}
          metaConversation={metaConversation}
          highlighted={false}
          asyncStatus={meta.requestAssistedTransfer}
          isAgent={true}
          presence={presence}
          onAssistedTransferClick={this.onAssistedTransferClick}
          onBlindTransferClick={this.onBlindTransferClick}
          numberTransferRequests={numberTransferRequests}
          transferToAgentRequests={transferToAgentRequests}
        />
      );
    };

    if (isLoadingQueueAgents) {
      loading = (
        <div className="row-item empty-list text-center">Loading agents...</div>
      );
    } else if (filteredPresence.length === 0) {
      loading = (
        <div className="row-item empty-list text-center">
          {queueId
            ? 'No online agents in this queue'
            : this.state.init
              ? 'Type a name to search for an agent'
              : 'No agent found'}
        </div>
      );
    }

    return (
      <>
        <Modal
          className={styles.modal}
          isOpen={ui.isModalShown}
          onClose={this.hideTransferToAgentModal}
        >
          <ModalHead
            title={`Transfer #${csid} to a different agent`}
            onClose={this.hideTransferToAgentModal}
          />
          <ModalBody>
            <FieldText
              input={{
                name: 'search',
                value: searchValue,
                onChange: this.onChangeInput,
              }}
              placeholder="Search online agents..."
              disabled={disabled}
              label="Search online agents"
            />
            <FieldWrapper label="Select an agent" noAlert>
              {filteredPresence.map(listAgent)}
              {loading}
            </FieldWrapper>
          </ModalBody>
          <ModalFoot
            cancelOnClick={
              queueId
                ? this.handleBackToQueueSelection
                : this.hideTransferToAgentModal
            }
            cancelName={queueId ? 'Back' : 'Cancel'}
          />
        </Modal>

        <Modal
          className={styles.modal}
          isOpen={ui.isMessageModalShown}
          onClose={this.hideTransferToAgentModalMessageModal}
        >
          <ModalHead
            title={`Transfer #${csid} to a different agent`}
            onClose={this.hideTransferToAgentModalMessageModal}
          />

          <ModalBody>
            <FieldTextarea
              input={{
                name: 'message',
                value: this.state.messageValue,
                onChange: this.onChangeMessageValue,
              }}
              label="Message"
              placeholder="Write a message..."
            />
          </ModalBody>
          <ModalFoot
            confirmOnClick={this.onBlindTransferMessageClick}
            confirmName="Transfer"
            cancelOnClick={this.hideTransferToAgentModalMessageModal}
            cancelName="Cancel transfer"
          />
        </Modal>
      </>
    );
  }
}
